import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import type { LucideIcon } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';
import { cn } from './ui/utils';

type Accent = 'cyan' | 'purple' | 'orange' | 'amber';

const ACCENT: Record<
  Accent,
  { border: string; text: string; hoverText: string; hoverBg: string; gradientFrom: string; gradientTo: string }
> = {
  cyan: {
    border: 'border-cyan-500/20',
    text: 'text-cyan-400',
    hoverText: 'hover:text-cyan-300',
    hoverBg: 'hover:bg-cyan-500/10',
    gradientFrom: 'from-cyan-400',
    gradientTo: 'to-purple-400',
  },
  purple: {
    border: 'border-purple-500/20',
    text: 'text-purple-400',
    hoverText: 'hover:text-purple-300',
    hoverBg: 'hover:bg-purple-500/10',
    gradientFrom: 'from-purple-400',
    gradientTo: 'to-cyan-400',
  },
  orange: {
    border: 'border-orange-500/20',
    text: 'text-orange-400',
    hoverText: 'hover:text-orange-300',
    hoverBg: 'hover:bg-orange-500/10',
    gradientFrom: 'from-orange-400',
    gradientTo: 'to-amber-400',
  },
  amber: {
    border: 'border-amber-500/20',
    text: 'text-amber-400',
    hoverText: 'hover:text-amber-300',
    hoverBg: 'hover:bg-amber-500/10',
    gradientFrom: 'from-amber-400',
    gradientTo: 'to-orange-400',
  },
};

export interface LegalMarkdownPageProps {
  onBack?: () => void;
  title: string;
  subtitle?: string;
  icon: LucideIcon;
  headerAccent?: Accent;
  documentAccent?: Accent;
  documentTitle?: string;
  markdown: string;
  lastUpdatedLabel?: string;
}

export function LegalMarkdownPage({
  onBack,
  title,
  subtitle,
  icon: Icon,
  headerAccent = 'cyan',
  documentAccent = 'purple',
  documentTitle = 'Document',
  markdown,
  lastUpdatedLabel,
}: LegalMarkdownPageProps) {
  const header = ACCENT[headerAccent];
  const doc = ACCENT[documentAccent];

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className={`bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b ${header.border}`}>
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex items-center gap-4 mb-4 sm:mb-6">
            <Button
              variant="ghost"
              className={`${header.text} ${header.hoverText} ${header.hoverBg} font-cyberpunk`}
              onClick={onBack || (() => window.history.back())}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Site
            </Button>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Icon className={`w-8 h-8 ${header.text}`} />
              <h1
                className={`text-4xl font-cyberpunk text-transparent bg-gradient-to-r ${header.gradientFrom} ${header.gradientTo} bg-clip-text`}
              >
                {title}
              </h1>
            </div>
            {subtitle ? <p className="text-gray-400 max-w-2xl mx-auto">{subtitle}</p> : null}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto">
          {lastUpdatedLabel ? (
            <Card className={`bg-slate-800/50 ${header.border} mb-8`}>
              <CardContent className="p-6">
                <div className={`flex items-center gap-3 ${header.text}`}>
                  <Icon className="w-5 h-5" />
                  <span className="font-cyberpunk">{lastUpdatedLabel}</span>
                </div>
              </CardContent>
            </Card>
          ) : null}

          <Card className={`bg-slate-800/30 ${doc.border} mb-8`}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-3 ${doc.text} font-cyberpunk`}>
                <Icon className="w-6 h-6" />
                {documentTitle}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="bg-slate-700/20 rounded-lg p-4 sm:p-6 border border-slate-500/10">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw, rehypeSanitize]}
                  components={{
                    h1: (props) => <h2 className="text-2xl sm:text-3xl font-cyberpunk text-white mb-4" {...props} />,
                    h2: (props) => <h3 className={`text-xl sm:text-2xl font-cyberpunk ${doc.text} mt-8 mb-3`} {...props} />,
                    h3: (props) => <h4 className={`text-lg sm:text-xl font-cyberpunk ${doc.text} mt-6 mb-2`} {...props} />,
                    h4: (props) => <h5 className="text-base sm:text-lg font-cyberpunk text-gray-100 mt-5 mb-2" {...props} />,
                    p: (props) => <p className="text-gray-300 leading-relaxed mb-4" {...props} />,
                    ul: (props) => <ul className="text-gray-300 leading-relaxed mb-4 pl-5 list-disc space-y-2" {...props} />,
                    ol: (props) => <ol className="text-gray-300 leading-relaxed mb-4 pl-5 list-decimal space-y-2" {...props} />,
                    li: (props) => <li className="text-gray-300" {...props} />,
                    a: ({ href, ...props }) => {
                      const isExternal = !!href && /^https?:\/\//i.test(href);
                      return (
                        <a
                          href={href}
                          className={`${doc.text} underline underline-offset-2 hover:opacity-90`}
                          target={isExternal ? '_blank' : undefined}
                          rel={isExternal ? 'noopener noreferrer' : undefined}
                          {...props}
                        />
                      );
                    },
                    hr: (props) => <hr className="border-slate-600/60 my-6" {...props} />,
                    blockquote: (props) => (
                      <blockquote className={`border-l-2 ${doc.border} pl-4 py-1 my-4 text-gray-300`} {...props} />
                    ),
                    table: ({ children, ...props }) => (
                      <div className="my-6 -mx-4 sm:mx-0 overflow-x-auto">
                        <table
                          {...props}
                          className={cn('min-w-[720px] w-full border-collapse text-left', props.className)}
                        >
                          {children}
                        </table>
                      </div>
                    ),
                    thead: (props) => <thead {...props} className={cn('bg-slate-800/60', props.className)} />,
                    th: (props) => (
                      <th
                        {...props}
                        className={cn(
                          'border border-slate-600/60 px-3 py-2 text-gray-100 font-cyberpunk text-sm align-top',
                          props.className
                        )}
                      />
                    ),
                    td: (props) => (
                      <td
                        {...props}
                        className={cn(
                          'border border-slate-700/60 px-3 py-2 text-gray-300 text-sm align-top',
                          props.className
                        )}
                      />
                    ),
                    code: (props) => (
                      <code className="px-1 py-0.5 rounded bg-slate-900/60 text-gray-100 text-sm" {...props} />
                    ),
                  }}
                >
                  {markdown}
                </ReactMarkdown>
              </div>
            </CardContent>
          </Card>

          {/* Footer Navigation */}
          <div className="text-center">
            <Button
              className={`bg-gradient-to-r ${headerAccent === 'orange' ? 'from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700' : headerAccent === 'purple' ? 'from-purple-500 to-cyan-600 hover:from-purple-600 hover:to-cyan-700' : 'from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700'} text-white px-8 py-3 rounded-full font-cyberpunk transition-all duration-300 transform hover:scale-105`}
              onClick={onBack || (() => window.history.back())}
            >
              Return to Site
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

